import React, { useEffect, useState } from "react";
import {
  useHistory,
  useLocation,
  useParams,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import ConfirmationDialog from "components/ConfirmationDialog";
import Popup from "components/Popup";
import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import Page from "components/Layout/Page";
import { Container as ContainerStyle, PublishStatusWrapper } from "./styles";
import FormControl from "components/FormControl";
import TopControls from "./TopControls";
import Controls from "./Controls";
import Form from "./Form";
import { observer } from "mobx-react";
import TriviaStore from "../TriviaStore";
import PublishStatus from "components/PublishStatus";

import ROUTES from "navigation/routes";
import { PUBLISH_STATUSES } from "constants/models";
import { SNACKBAR_TYPES } from "components/Snackbar";

type Props = SnackbarContextProps & {};

const TriviaDetails = ({ setMessage }: Props) => {
  const location = useLocation();
  const params = useParams();
  const history = useHistory();

  const publishStatus = TriviaStore.details?.publishStatus;

  const [editMode, setEditMode] = useState(false);
  const [isPopupDirty, setIsPopupDirty] = useState(false);
  const [isNewTrivia, setIsNewTrivia] = useState(false);
  const [triviaId, setTriviaId] = useState<number | null>(null);
  const [updateConfirmationIsOpened, setUpdateConfirmationIsOpened] = useState(
    false,
  );
  const [closeConfirmationIsOpened, setCloseConfirmationIsOpened] = useState(
    false,
  );
  const [onAccept, setOnAccept] = useState<() => void>(() => {});

  const onCloseForm = (onAccept: () => void) => {
    if (isPopupDirty) {
      setCloseConfirmationIsOpened(true);
      setOnAccept(() => onAccept);
    } else {
      onAccept();
    }
  };

  const onBackClick = () => {
    const onAccept = () => history.push(ROUTES.CONTENT_TRIVIA);

    if (editMode) {
      onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  const exitEditMode = (triviaId: number) => {
    const path = generatePath(ROUTES.CONTENT_TRIVIA_DETAIL, {
      triviaId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
    setEditMode(false);
  };

  const onCancelClick = (resetForm: () => void) => {
    if (isNewTrivia) {
      onBackClick();
    } else {
      const onAccept = () => {
        if (triviaId == null) return;
        exitEditMode(triviaId);
        setCloseConfirmationIsOpened(false);
        resetIsPopupDirty();
        resetForm();
      };
      onCloseForm(onAccept);
    }
  };

  useEffect(() => {
    const { triviaId } = params;
    setTriviaId(triviaId);
    const isNewTrivia = triviaId == null;
    setIsNewTrivia(isNewTrivia);

    if (isNewTrivia) {
      history.replace({ state: { editMode: true } });
      setEditMode(true);
      return;
    }

    setEditMode(location.state?.editMode || false);
    TriviaStore.fetchTriviaDetails(triviaId);
  }, []);

  const openUpdateConfirmation = () => {
    setUpdateConfirmationIsOpened(true);
  };

  const closeUpdateConfirmation = () => {
    setUpdateConfirmationIsOpened(false);
  };

  const confirmUpdate = (values: any) => {
    closeUpdateConfirmation();
    submit(values);
  };

  const onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    if (dirty === isPopupDirty) return;
    setIsPopupDirty(dirty);
  };

  const resetIsPopupDirty = () => {
    setIsPopupDirty(false);
  };

  const submit = async (values: any) => {
    const { id, title, code } = values;
    const data = {
      title: title?.trim(),
      code: code?.trim(),
    };
    try {
      if (isNewTrivia) {
        const id = await TriviaStore.createTrivia(data);
        if (!id) throw new Error("Try again later");
        const path = generatePath(ROUTES.CONTENT_TRIVIA, {
          triviaId: id,
        });
        history.push({ pathname: path, state: { editMode: false } });
        TriviaStore.fetchTriviaDetails(id);
      } else {
        const payload = { ...data, id };
        await TriviaStore.editTrivia(payload);
        exitEditMode(id);
        await TriviaStore.fetchTriviaDetails(id);
      }
      setIsPopupDirty(false);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  const onEditClick = () => {
    const path = generatePath(ROUTES.CONTENT_TRIVIA_DETAIL, {
      triviaId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
    setEditMode(true);
  };

  const onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      publishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(publishStatus);

    shouldShowUpdateConfirmation ? openUpdateConfirmation() : submit(values);
  };

  const onAddQuestionPopupSubmit = async (values: any) => {
    const { title, code, complement, isRequired } = values;
    const data = {
      title: title?.trim(),
      code: code?.trim(),
      complement: complement?.trim(),
      isRequired: isRequired === "true",
      responseType: "Unique",
      triviaID: +(triviaId || -1),
      displayOrder: 0,
    };
    try {
      const id = await TriviaStore.addQuestionToTrivia(data);
      if (!id) throw new Error("Try again later");
      TriviaStore.fetchTriviaDetails(+(triviaId || -1));
      setIsPopupDirty(false);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  return (
    <Container header={<Header controls={<PublishButton />} />}>
      <Page
        title="Trivia Details"
        leftControls={
          publishStatus && (
            <PublishStatusWrapper>
              <PublishStatus status={publishStatus} />
            </PublishStatusWrapper>
          )
        }
        isLoading={TriviaStore.isTriviaLoading}
        topControls={
          <TopControls onBackClick={onBackClick} text={"Back to Trivias"} />
        }
      >
        {closeConfirmationIsOpened && (
          <DiscardChangesPopup
            onClose={() => setCloseConfirmationIsOpened(false)}
            onAccept={onAccept}
          />
        )}

        <FormControl
          onSubmit={onSubmit}
          initialValues={isNewTrivia ? {} : TriviaStore.details?.formData}
          keepDirtyOnReinitialize={isNewTrivia}
        >
          {updateConfirmationIsOpened && (
            <ProceedToSavePopup
              onClose={closeUpdateConfirmation}
              onAccept={confirmUpdate}
            />
          )}

          <FormSpy
            subscription={{ dirty: true }}
            onChange={onChangeIsPopupDirty}
          />

          <ContainerStyle>
            <Controls
              isLoading={TriviaStore.isTriviaManaging}
              isEditMode={editMode}
              isNew={isNewTrivia}
              onCancelClick={onCancelClick}
              onEditClick={onEditClick}
            />
            <Form
              isEditMode={editMode}
              triviaId={triviaId}
              isNewTrivia={isNewTrivia}
              displayValues={TriviaStore.details}
              onQuestionSubmit={onAddQuestionPopupSubmit}
            />
          </ContainerStyle>
        </FormControl>
      </Page>
    </Container>
  );
};

const DiscardChangesPopup = ({
  onClose,
  onAccept,
}: {
  onClose: () => void;
  onAccept: () => void;
}) => {
  return (
    <Popup onClose={onClose} withoutExitButton>
      <ConfirmationDialog
        title="Discard Changes"
        text="Are you sure you would like to discard changes? All information will be lost."
        declineText="Cancel"
        acceptText="Discard"
        onDecline={onClose}
        onAccept={onAccept}
      />
    </Popup>
  );
};

const ProceedToSavePopup = ({ onClose, onAccept }: any) => {
  return (
    <FormSpy subscription={{ values: true }}>
      {({ values }) => (
        <Popup onClose={onClose} withoutExitButton>
          <ConfirmationDialog
            title="Save Changes to Production"
            text="All changes made will be published to production. Are you sure you want to proceed?"
            acceptText="Proceed"
            declineText="Cancel"
            onAccept={() => onAccept(values)}
            onDecline={onClose}
          />
        </Popup>
      )}
    </FormSpy>
  );
};

export default withGlobalSnackbar(observer(TriviaDetails));
